import { useQuery } from '@tanstack/react-query'
import { getQontoStatus, qontoApiCall, QontoAuthError } from '@/services/qontoApi'
import { useQontoConnect } from '@/hooks/useQontoOAuth'
import { formatCurrency } from '@/lib/utils'
import { Landmark, WifiOff, Loader2, LogIn } from 'lucide-react'

interface BankAccount {
  slug: string
  iban: string
  bic: string
  currency: string
  balance: number
  balance_cents: number
  authorized_balance: number
  authorized_balance_cents: number
  name: string
}

interface OrganizationResponse {
  organization: {
    slug: string
    legal_name: string
    bank_accounts: BankAccount[]
  }
}

/**
 * Displays Qonto bank account balance inline in stat grids.
 * Shows connection status if not connected.
 */
export function QontoBalanceCard() {
  const { connect, isLoading: isConnecting } = useQontoConnect()

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['qonto', 'connection-status'],
    queryFn: getQontoStatus,
    staleTime: 5 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  const { data: orgData, isLoading: orgLoading, error: orgError } = useQuery({
    queryKey: ['qonto', 'organization'],
    queryFn: () => qontoApiCall<OrganizationResponse>('/organization'),
    enabled: !!status?.connected,
    staleTime: 5 * 60_000,
    retry: (failureCount, error) => error instanceof QontoAuthError ? false : failureCount < 1,
    refetchOnWindowFocus: false,
  })

  const isLoading = statusLoading || orgLoading

  // Not connected — show connect button
  if (!statusLoading && !status?.connected) {
    return (
      <div className="rounded-xl p-4 bg-beige-50 text-brown-400">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium opacity-80">Solde Qonto</p>
            <button
              onClick={() => connect()}
              disabled={isConnecting}
              className="mt-1 flex items-center gap-1.5 text-sm font-medium text-mint-600 hover:text-mint-700 transition-colors disabled:opacity-50"
            >
              {isConnecting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <LogIn className="w-3.5 h-3.5" />
              )}
              {isConnecting ? 'Connexion...' : 'Connecter Qonto'}
            </button>
          </div>
          <div className="opacity-40">
            <Landmark className="w-6 h-6" />
          </div>
        </div>
      </div>
    )
  }

  // Loading
  if (isLoading) {
    return (
      <div className="rounded-xl p-4 bg-mint-50 text-mint-600">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium opacity-80">Solde Qonto</p>
            <div className="flex items-center gap-2 mt-1">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Chargement...</span>
            </div>
          </div>
          <div className="opacity-60">
            <Landmark className="w-6 h-6" />
          </div>
        </div>
      </div>
    )
  }

  // Token expired — show warning instead of stale data
  const isExpiredByDate = status?.connected && status?.expires_at && status.expires_at * 1000 < Date.now()
  const isExpiredByApi = status?.connected && orgError instanceof QontoAuthError
  const isTokenExpired = isExpiredByDate || isExpiredByApi
  if (isTokenExpired) {
    return (
      <div className="rounded-xl p-4 bg-gold-50 text-gold-700">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium opacity-80">Solde Qonto</p>
            <button
              onClick={() => connect()}
              disabled={isConnecting}
              className="mt-1 flex items-center gap-1.5 text-sm font-medium text-gold-700 hover:text-gold-800 transition-colors disabled:opacity-50"
            >
              {isConnecting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <WifiOff className="w-3.5 h-3.5" />
              )}
              {isConnecting ? 'Connexion...' : 'Token expire — Reconnecter'}
            </button>
          </div>
          <div className="opacity-40">
            <Landmark className="w-6 h-6" />
          </div>
        </div>
      </div>
    )
  }

  // Connected — show balance
  const mainAccount = orgData?.organization?.bank_accounts?.[0]
  const balance = mainAccount?.balance ?? 0
  const accountName = mainAccount?.name || 'Compte principal'

  return (
    <div className="rounded-xl p-4 bg-mint-50 text-mint-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">Solde Qonto</p>
          <p className="text-2xl font-bold mt-1">{formatCurrency(balance)}</p>
          <p className="text-xs opacity-60 mt-0.5">{accountName}</p>
        </div>
        <div className="opacity-60">
          <Landmark className="w-6 h-6" />
        </div>
      </div>
    </div>
  )
}
